import { useEffect, useState } from "react";
import { TrendingUp, Users, Send, Building2, DollarSign, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/* Founder analytics widget — top of /admin/studio-invites.
   Pulls GET /admin/lumetier/metrics (founder-only) and renders four KPIs +
   a funnel diagram + an 8-week signup sparkline.
   Silently no-ops when the requester isn't the platform owner (403). */
export default function LumetierInsights() {
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/lumetier/metrics")
      .then((r) => setM(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err) return null;
  if (!m) {
    return <div className="bg-card border border-border h-32 animate-pulse" data-testid="insights-loading" />;
  }

  const f = m.funnel;
  const maxSignup = Math.max(1, ...m.signups_by_week.map((w) => w.count));
  const totalSignups8w = m.signups_by_week.reduce((acc, w) => acc + w.count, 0);

  // Funnel rows — each one shows count + conversion-rate-from-prior
  const funnel = [
    { label: "Waitlist signups",   value: f.waitlist_total,    icon: Users,     fromPrev: null },
    { label: "Converted to invite", value: f.waitlist_converted, icon: Send,     fromPrev: f.waitlist_total },
    { label: "Onboarded studios",  value: f.invites_accepted,  icon: Building2, fromPrev: f.waitlist_converted },
    { label: "Active last 30d",    value: f.active_30d,        icon: TrendingUp, fromPrev: f.invites_accepted },
  ];

  return (
    <section data-testid="lumetier-insights" className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-1.5 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Lumetier · Insights
          </div>
          <h2 className="text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Your weekly story
          </h2>
        </div>
        <div className="text-xs text-muted-foreground hidden sm:block">
          As of {new Date(m.as_of).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="insights-kpis">
        <KPI
          icon={DollarSign}
          label="Estimated MRR"
          value={`$${m.estimated_mrr_usd.toLocaleString()}`}
          sub={`from ${m.studios.active} active ${m.studios.active === 1 ? "studio" : "studios"}`}
        />
        <KPI
          icon={Users}
          label="Last 8 weeks"
          value={totalSignups8w}
          sub={`waitlist signup${totalSignups8w === 1 ? "" : "s"}`}
        />
        <KPI
          icon={Send}
          label="Invites pending"
          value={f.invites_pending}
          sub={`${f.invites_accepted} accepted total`}
        />
        <KPI
          icon={Building2}
          label="Active studios"
          value={m.studios.active}
          sub={m.studios.suspended > 0 ? `${m.studios.suspended} suspended` : "no suspensions"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Funnel */}
        <div className="bg-card border border-border p-5" data-testid="insights-funnel">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-4">
            Conversion funnel
          </div>
          <div className="space-y-2">
            {funnel.map((row) => {
              const widthPct = Math.max(4, Math.round((row.value / Math.max(1, f.waitlist_total)) * 100));
              const convPct = row.fromPrev != null && row.fromPrev > 0
                ? Math.round((row.value / row.fromPrev) * 100)
                : null;
              return (
                <div key={row.label} className="flex items-center gap-3" data-testid={`funnel-row-${row.label.split(" ")[0].toLowerCase()}`}>
                  <row.icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-xs mb-1 gap-2">
                      <span className="truncate">{row.label}</span>
                      <span className="tabular-nums font-medium">{row.value}</span>
                    </div>
                    <div className="h-1.5 bg-muted/60 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                  {convPct != null ? (
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums w-12 text-right">
                      {convPct}%
                    </div>
                  ) : <div className="w-12" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sparkline */}
        <div className="bg-card border border-border p-5" data-testid="insights-sparkline">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-4">
            Waitlist signups · last 8 weeks
          </div>
          {totalSignups8w === 0 ? (
            <div className="text-sm text-muted-foreground italic py-6">
              No waitlist signups yet — share <a href="/lumetier" className="underline">/lumetier</a> to start collecting leads.
            </div>
          ) : (
            <div className="flex items-end gap-1.5 h-24" data-testid="sparkline-bars">
              {m.signups_by_week.map((w) => (
                <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end" title={`Week of ${w.label}: ${w.count} signup${w.count === 1 ? "" : "s"}`}>
                  <div className="text-[10px] text-muted-foreground tabular-nums mb-1">
                    {w.count > 0 ? w.count : ""}
                  </div>
                  <div
                    className={`w-full ${w.count > 0 ? "bg-foreground" : "bg-muted"} transition-all`}
                    style={{ height: `${Math.max(3, (w.count / maxSignup) * 70)}px` }}
                  />
                  <div className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mt-1.5">
                    {w.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Plan distribution row — only render if any tenants exist */}
      {Object.keys(m.plan_distribution).length > 0 && (
        <div className="bg-card border border-border p-5" data-testid="insights-plans">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-4">
            Plan distribution
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {Object.entries(m.plan_distribution).map(([plan, count]) => (
              <div key={plan} className="flex items-baseline gap-2" data-testid={`plan-dist-${plan}`}>
                <span className="text-2xl font-light tabular-nums">{count}</span>
                <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  {plan}{m.plan_prices[plan] > 0 ? ` · $${m.plan_prices[plan]}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-3xl font-light mt-1 tabular-nums" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
